import type { Gate, Sighting, Task, WorkItem } from './types';

export const shortDate = (ts: string | null | undefined): string => (ts ? ts.slice(0, 10) : '');

// shortActor turns "dirk@reality.tech via claude" into "dirk via claude".
export function shortActor(by: string | null | undefined): string {
  if (!by) return '';
  const [who, via] = by.split(' via ');
  const name = who.includes('@') ? who.slice(0, who.indexOf('@')) : who;
  return via ? `${name} via ${via}` : name;
}

// taskWho is a task's responsibility line: added by, started by, done by.
export function taskWho(t: {
  state: string;
  by: string | null;
  done_at: string | null;
  created_by?: string | null;
  started_by?: string | null;
  started_at?: string | null;
}): string {
  const parts: string[] = [];
  if (t.created_by) parts.push(`added by ${shortActor(t.created_by)}`);
  if (t.started_by) parts.push(`started by ${shortActor(t.started_by)} ${shortDate(t.started_at)}`);
  if (t.by && (t.state === 'done' || t.state === 'dropped')) parts.push(`${t.state} by ${shortActor(t.by)} ${shortDate(t.done_at)}`);
  return parts.join(' · ');
}

// Days from today (local) to a YYYY-MM-DD date; negative when it has passed.
export function daysUntil(due: string): number {
  const [y, m, d] = due.split('-').map(Number);
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return Math.round((new Date(y, m - 1, d).getTime() - start) / 86_400_000);
}

// dueLabel reads a due date the way a person would say it, with a tone for colour.
export function dueLabel(due: string | null | undefined): { text: string; tone: 'overdue' | 'soon' | 'later' } | null {
  if (!due) return null;
  const n = daysUntil(due);
  if (n < 0) return { text: n === -1 ? 'due yesterday' : `${-n} days overdue`, tone: 'overdue' };
  if (n === 0) return { text: 'due today', tone: 'soon' };
  if (n === 1) return { text: 'due tomorrow', tone: 'soon' };
  if (n < 7) return { text: `due in ${n} days`, tone: 'soon' };
  return { text: `due ${due}`, tone: 'later' };
}

export function timeAgo(ts: string | null | undefined): string {
  if (!ts) return '';
  const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 31) return `${days} days ago`;
  const months = Math.round(days / 30.4);
  return months < 24 ? `${months} months ago` : `${Math.round(days / 365)} years ago`;
}

// A bug that shipped but has no resolution yet is under watch, not finished:
// its fix is waiting for a clean window of sightings.
export function boardGroup(item: WorkItem): string {
  if (item.kind === 'bug' && item.status === 'released' && !item.resolution) return 'monitoring';
  return item.status;
}

export const GROUP_ORDER = [
  'in_progress',
  'built',
  'fixed',
  'monitoring',
  'released',
  'verified',
  'planned',
  'open',
  'parked',
];

export const GROUP_LABEL: Record<string, string> = {
  in_progress: 'In progress',
  built: 'Built',
  fixed: 'Fixed',
  monitoring: 'Under watch',
  released: 'Released',
  verified: 'Verified',
  planned: 'Planned',
  open: 'Open',
  parked: 'Parked',
};

export const gateDone = (gates: Gate[], gate: string, channel = 'prod'): boolean =>
  gates.some((g) => g.gate === gate && g.channel === channel && (g.state === 'done' || g.state === 'n/a'));

export const openTaskCount = (tasks: Task[]): number =>
  tasks.filter((t) => t.state === 'todo' || t.state === 'doing').length;

export function remainingGates(item: WorkItem, gates: Gate[]): string[] {
  return item.required_gates.filter((g) => !gateDone(gates, g));
}

// Headset shells arrive as a list, one per connected headset; the lowest one is
// what decides whether a sighting counts as post-fix.
export function versionsSummary(versions: Sighting['versions']): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(versions ?? {})) {
    if (k === 'captured') continue;
    parts.push(`${k.startsWith('experience:') ? 'experience' : k} ${Array.isArray(v) ? v.join('/') : v}`);
  }
  return parts.join(', ');
}
